import React, { useMemo } from 'react'
import {
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material'
import type { Auction } from '@otw/models'
import { getUserDisplayName, useUsers } from 'hooks/useUsers'

export interface AuctionTableProps {
  auctions: Auction[]
  onEdit: (auction: Auction) => void
  onDelete: (id: string) => void
}

const formatCreatedAt = (value: Auction['created_at']): string => {
  if (!value) return '—'
  let date: Date | null = null
  if (value instanceof Date) {
    date = value
  } else if (typeof value === 'object' && value !== null && 'toDate' in value) {
    const maybe = (value as { toDate?: () => Date }).toDate?.()
    if (maybe instanceof Date) date = maybe
  } else if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value)
    if (!Number.isNaN(parsed.getTime())) date = parsed
  }
  if (!date) return '—'
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date)
}

const AuctionTable: React.FC<AuctionTableProps> = ({ auctions, onEdit, onDelete }) => {
  const { users } = useUsers()

  const userNameById = useMemo(() => {
    const map = new Map<string, string>()
    for (const user of users) {
      map.set(String(user.id), getUserDisplayName(user))
    }
    return map
  }, [users])

  if (auctions.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No auctions yet. Add auction houses here and set whether gate passes
          are required.
        </Typography>
      </Paper>
    )
  }

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Gate passes</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Created by</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {auctions.map((auction) => (
            <TableRow key={auction.id} hover>
              <TableCell>
                <Typography variant="body2" fontWeight={600}>
                  {auction.name}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={auction.gate_passes_needed ? 'Needed' : 'Not needed'}
                  color={auction.gate_passes_needed ? 'warning' : 'default'}
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {formatCreatedAt(auction.created_at)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {auction.created_by
                    ? userNameById.get(String(auction.created_by)) || 'Unknown'
                    : '—'}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={() => onEdit(auction)}
                      aria-label="Edit auction"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => auction.id && onDelete(auction.id)}
                      aria-label="Delete auction"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default AuctionTable
